/**
 * Mirrors the codes mm-be can put in `error.code`:
 *
 *   services/common/exceptions.py   the ServiceError base and its generic subclasses
 *   services/auth/exceptions.py     the auth slice's own codes
 *   api/v5/exception_handler.py     DRF's own failures (auth, throttling) mapped onto codes
 *
 * `code` is the contract; `message` is not. The backend may reword any message
 * freely, so the client must branch on `code` and never string-match English.
 * Adding a code here is backwards-compatible; removing one is not.
 */
export const ERROR_CODES = [
  // services/common/exceptions.py + api/v5/exception_handler.py
  'VALIDATION_ERROR',
  'AUTHENTICATION_REQUIRED',
  'PERMISSION_DENIED',
  'NOT_FOUND',
  'SUBSCRIPTION_REQUIRED',
  'RATE_LIMITED',
  'INTERNAL_ERROR',
  'UPSTREAM_UNAVAILABLE',

  // services/auth/exceptions.py
  'INVALID_CREDENTIALS',
  'ACCOUNT_NOT_VERIFIED',
  'USER_NOT_FOUND',
  'OTP_INVALID',
  'EMAIL_ALREADY_EXISTS',
  'PHONE_ALREADY_EXISTS',
  'RESEND_TOO_SOON',

  // services/devices/exceptions.py
  'NO_PUSH_DEVICES',
  'PUSH_DISABLED',
  'TEST_PUSH_TOKEN_REQUIRED',
  'PUSH_DEVICE_NOT_FOUND',
  'SOCIAL_TOKEN_INVALID',
  'PASSWORD_INCORRECT',
  'CONFIRMATION_REQUIRED',
  'PASSWORD_TOO_WEAK',

  // services/public_issues/exceptions.py
  'ISSUE_NOT_FOUND',

  // services/subscriptions/exceptions.py — all but the two the RevenueCat
  // webhook raises, which only a server-to-server call can receive.
  'PLAN_NOT_FOUND',
  'PLAN_NOT_PURCHASABLE',
  'DISCOUNT_NOT_APPLICABLE',
  'PAYMENT_GATEWAY_UNAVAILABLE',
  'UNSUPPORTED_PAYMENT_METHOD',
  'PAYMENT_NOT_FOUND',
  'PAYMENT_NOT_VERIFIED',
  'PAYMENT_AMOUNT_MISMATCH',

  // services/courses/exceptions.py — the purchase surface (`checkout/`,
  // `enrol/`). COURSE_NOT_FOUND is the 404 they share with the course read;
  // the other read-side codes (certificates, the path, the dictionary) are
  // branched on by name in features/courses/api/queries.ts and not listed.
  // COURSE_IS_FREE means "enrol instead" and COURSE_NOT_FREE "check out
  // instead", so neither is a dead end; COURSE_NOT_PURCHASABLE is a fault in
  // the course's own data that the member cannot fix. COURSE_PAYMENTS_DISABLED
  // is every course switched off, free enrolment too, where
  // PAYMENT_GATEWAY_UNAVAILABLE is one gateway. It never refuses the confirm
  // of a payment already taken.
  'COURSE_NOT_FOUND',
  'COURSE_ALREADY_OWNED',
  'COURSE_NOT_PURCHASABLE',
  'COURSE_IS_FREE',
  'COURSE_NOT_FREE',
  'COURSE_PAYMENTS_DISABLED',

  // services/company/exceptions.py — 404 for an unknown symbol on every
  // /company/{symbol}/… read (and on compare, for any one of its symbols).
  'COMPANY_NOT_FOUND',

  // services/datafeed/exceptions.py — the company statement, metrics and
  // Advanced-panel reads, which go through the external data service.
  // PANEL_NOT_AVAILABLE_FOR_SECTOR is a 404 about one Advanced tab, not about
  // the company; the four DATA_FEED_* codes are all 503s, and only
  // DATA_FEED_UNAVAILABLE is worth a retry.
  'PANEL_NOT_AVAILABLE_FOR_SECTOR',
  'INVALID_PANEL_PERIOD',
  'DATA_FEED_UNAVAILABLE',
  'DATA_FEED_NOT_ASSIGNED',
  'DATA_FEED_NOT_FOUND',
  'DATA_FEED_RATE_LIMITED',

  // services/insights/exceptions.py — Mitra's take on the Stock page
  // (`/company/{symbol}/insights/…`). INSIGHTS_COMPANY_NOT_COVERED is a 404 for
  // a fund, debenture or index, which have no takes; INSIGHTS_UNKNOWN_PAGE is
  // a 404 for a page key mm-be does not write; INSIGHTS_NOT_READY is a 409 for
  // a vote on a take that is not ready.
  'INSIGHTS_COMPANY_NOT_COVERED',
  'INSIGHTS_UNKNOWN_PAGE',
  'INSIGHTS_NOT_READY',

  // services/market/exceptions.py — `/market/sectors/{symbol}/stats/`.
  'SECTOR_NOT_FOUND',

  // Anticipated by the market and portfolio slices. No mm-be endpoint raises
  // these yet; they are kept so the screens written against them still type.
  'MARKET_CLOSED',
  'DATA_UNAVAILABLE',
  'SHAREHOLDER_NOT_FOUND',
  'HOLDING_NOT_FOUND',
  'TRANSACTION_NOT_FOUND',
  'INSUFFICIENT_QUANTITY',
] as const

export type ErrorCode = (typeof ERROR_CODES)[number]

/** Anything the backend sends that this client build predates. */
export type AnyErrorCode = ErrorCode | (string & {})

export type FieldErrors = Record<string, string[]>

export class ApiError extends Error {
  readonly code: AnyErrorCode
  readonly fields: FieldErrors
  readonly status: number

  constructor(args: { code: AnyErrorCode; message: string; fields?: FieldErrors; status: number }) {
    super(args.message)
    this.name = 'ApiError'
    this.code = args.code
    this.fields = args.fields ?? {}
    this.status = args.status
  }

  /**
   * 4xx other than 429 will not succeed on retry — see query-client.ts.
   *
   * That includes 401: getData/getPage already refresh and replay an expired
   * session once, so a 401 that reaches a caller means the session is gone.
   */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500
  }

  is(code: AnyErrorCode): boolean {
    return this.code === code
  }
}

/** Network failure, CORS rejection, or a body that was not the v5 envelope. */
export class NetworkError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message)
    this.name = 'NetworkError'
  }
}

/**
 * A success whose body was not the kind of file asked for — an HTML page from
 * a proxy or a login wall where a PDF was due. A NetworkError, so every
 * existing "the server's answer was unusable" branch already covers it; its
 * own class so a caller can tell it apart.
 */
export class FileTypeError extends NetworkError {
  constructor(
    readonly expected: string,
    readonly received: string,
  ) {
    super(`Expected a ${expected} file, got ${received === '' ? 'no type' : received}.`)
    this.name = 'FileTypeError'
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError

export const hasCode = (e: unknown, code: AnyErrorCode): boolean => isApiError(e) && e.code === code

/** The paywall branch. Tier logic lives in the backend; the client reads one code. */
export const isPaywall = (e: unknown): boolean => hasCode(e, 'SUBSCRIPTION_REQUIRED')

/**
 * The session is over: the token was missing, expired or rejected, and (when
 * it came through getData/getPage) the one refresh-and-replay also failed.
 *
 * Deliberately NOT true for INVALID_CREDENTIALS or SOCIAL_TOKEN_INVALID. Both
 * are 401s too, but they mean "that sign-in attempt was wrong", not "your
 * session ended" — branching on status would conflate them.
 */
export const isAuthExpired = (e: unknown): boolean => hasCode(e, 'AUTHENTICATION_REQUIRED')
